import { ProCard } from '@ant-design/pro-components'
import { db } from '@app/icare/client/db'
import { localResultQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { ArticleCategories } from '@app/icare/db/fixture/article_category'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientOrderFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import type { ArticleIdType, ArticleRelation } from '@app/icare/entities/article/schema/Article'
import { ArticleRelationEditDrawer } from '@app/icare/routes/_app/artikel/-components/ArticleRelationEditDrawer'
import { displayArticle } from '@app/icare/routes/_app/artikel/-utils/displayArticle'
import { displayCategory } from '@app/icare/routes/_app/artikel/-utils/displayCategory'
import { useArticles } from '@app/icare/routes/_app/artikel/-utils/useArticles'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { CreateButton } from '@pkg/ui/button'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Table } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { useMemo } from 'react'
import { FaPencil } from 'react-icons/fa6'

export const Route = createFileRoute('/_app/artikel/$id/relations')({
  component: RouteComponent,
})

function RouteComponent() {
  const articleId = Route.useParams().id as ArticleIdType

  const { data: relations = [] } = useQuery(
    localResultQueryOptions({
      query: db
        .selectFrom('articleRelation')
        .selectAll()
        .where('articleId', '=', articleId)
        .$call(queryKit.deleted('exclude')),
    }),
  )

  const articles = useArticles()

  const columns: ColumnType<ArticleRelation>[] = useMemo(
    () => [
      {
        key: 'type',
        dataIndex: 'type',
        title: t`Typ`,
        render: type => (type === 'article' ? t`Artikel` : t`Kategorie`),
      },
      {
        key: 'toId',
        dataIndex: 'toId',
        title: t`Zugehörigkeit`,
        render: (toId: ArticleRelation['toId'], { type }) =>
          type === 'article'
            ? displayArticle(articles.find(({ id }) => id === toId))
            : displayCategory(ArticleCategories.find(({ id }) => id === toId)),
      },
      {
        key: 'forms',
        dataIndex: 'forms',
        title: t`Bedarfsermittlung`,
        render: (forms: ArticleRelation['forms']) =>
          forms.map(form => PatientOrderFormTypeEnum.$.getLabel(form)).join(', '),
      },
      {
        key: 'actions',
        width: 80,
        render: (_, relation) => (
          <Button
            type="text"
            size="small"
            icon={<FaPencil />}
            onClick={() => void ArticleRelationEditDrawer.show({ articleId, id: relation.id })}
          />
        ),
      },
    ],
    [articles, articleId],
  )

  return (
    <ProCard headerBordered title="Beziehungen" className="mx-4">
      <Table rowKey="id" dataSource={relations as ArticleRelation[]} columns={columns} />
      <CreateButton onClick={() => void ArticleRelationEditDrawer.show({ articleId })}>
        <Trans>Beziehung hinzufügen</Trans>
      </CreateButton>
    </ProCard>
  )
}
